import { z } from 'zod'
import { VertexSchema } from '../vertices'
import { EdgeSchema } from '../edges'
import { FluidZoneSchema } from '../document/fluid-zone'
import { PipeSpecificationSchema } from '../document/pipe-specification'
import { HeatTransferSettingsSchema } from '../document/heat-transfer-settings'
import { LayerSchema } from '../document/layer'
import { CustomFieldDefinitionSchema } from '../document/custom-field-definition'

export const DeviceLibrarySnapshotSchema = z.object({
  schema_version: z.literal(2),
  vertices: z.array(VertexSchema).min(1),
  edges: z.array(EdgeSchema),
  fluid_zones: z.array(FluidZoneSchema),
  pipe_specifications: z.array(PipeSpecificationSchema),
  heat_transfer_settings: z.array(HeatTransferSettingsSchema),
  layers: z.array(LayerSchema),
  custom_field_definitions: z.array(CustomFieldDefinitionSchema),
})

export type DeviceLibrarySnapshot = z.infer<typeof DeviceLibrarySnapshotSchema>

/**
 * Returns a list of human-readable closure violations (empty = closed). Every
 * cross-reference in the snapshot must resolve INSIDE the snapshot (spec §3).
 * Used by the backend to reject a bad POST and by the frontend snapshot builder
 * to reject an incomplete selection with a specific message.
 *
 * `data` is typed loosely (the per-element schemas already validate shape) so
 * this can run on a parsed snapshot without re-narrowing every device union.
 */
export function validateSnapshotClosure(data: DeviceLibrarySnapshot): string[] {
  const errs: string[] = []
  // Cast to any[] so the closure walker can access dynamic per-device-type fields
  // (layer_id, penetrations, linked_device_id, operation.linked_device_id, etc.)
  // without narrowing every union branch. The per-element schemas already validated
  // shape; this function only checks cross-reference resolution.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v = data.vertices as any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = data.edges as any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fzIds = new Set<string>(data.fluid_zones.map((x) => (x as any).id as string))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const specById = new Map<string, any>(data.pipe_specifications.map((s) => [(s as any).id as string, s]))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const htsIds = new Set<string>(data.heat_transfer_settings.map((x) => (x as any).id as string))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layerIds = new Set<string>(data.layers.map((x) => (x as any).id as string))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cfdIds = new Set<string>(data.custom_field_definitions.map((x) => (x as any).id as string))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vertexIds = new Set<string>((v as any[]).map((x: any) => x.id as string))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const edgeIds = new Set<string>((e as any[]).map((x: any) => x.id as string))

  // Edges → vertices / fluid_zones / pipe specs (+ nested size) / heat-transfer / layer / custom fields
  for (const edge of e) {
    if (!vertexIds.has(edge.connection?.source_id)) errs.push(`Edge ${edge.id} source ${edge.connection?.source_id} not in selection`)
    if (!vertexIds.has(edge.connection?.target_id)) errs.push(`Edge ${edge.id} target ${edge.connection?.target_id} not in selection`)
    if (edge.fluid_zone_id !== undefined && !fzIds.has(edge.fluid_zone_id)) errs.push(`Edge ${edge.id} fluid zone ${edge.fluid_zone_id} missing`)
    if (edge.type === 'pipe' || edge.type === 'compressible_pipe') {
      const spec = specById.get(edge.pipe_specification_id)
      if (!spec) {
        errs.push(`Edge ${edge.id} pipe specification ${edge.pipe_specification_id} missing`)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if (edge.pipe_size_id !== undefined && !(spec.sizes ?? []).some((s: any) => s.id === edge.pipe_size_id)) {
        errs.push(`Edge ${edge.id} pipe size ${edge.pipe_size_id} not in specification ${edge.pipe_specification_id}`)
      }
    }
    if (edge.heat_transfer_settings_id !== undefined && !htsIds.has(edge.heat_transfer_settings_id)) errs.push(`Edge ${edge.id} heat-transfer settings ${edge.heat_transfer_settings_id} missing`)
    if (edge.layer_id !== undefined && !layerIds.has(edge.layer_id)) errs.push(`Edge ${edge.id} layer ${edge.layer_id} missing`)
    for (const key of Object.keys(edge.custom_field_values ?? {})) if (!cfdIds.has(key)) errs.push(`Edge ${edge.id} custom-field definition ${key} missing`)
  }

  // Vertices: layer / custom fields (all) + per-type graph & ref-data references
  for (const vx of v) {
    if (vx.layer_id !== undefined && !layerIds.has(vx.layer_id)) errs.push(`Device ${vx.id} layer ${vx.layer_id} missing`)
    for (const key of Object.keys(vx.custom_field_values ?? {})) if (!cfdIds.has(key)) errs.push(`Device ${vx.id} custom-field definition ${key} missing`)

    if (vx.type === 'tank') {
      if (vx.fluid_zone_id !== undefined && !fzIds.has(vx.fluid_zone_id)) errs.push(`Tank ${vx.id} fluid zone ${vx.fluid_zone_id} missing`)
      for (const p of vx.penetrations ?? []) if (!edgeIds.has(p.edge_id)) errs.push(`Tank ${vx.id} connects to pipe ${p.edge_id}, which isn't in your selection — add it or remove the tank`)
    }
    if (vx.type === 'three_way_valve') {
      for (const f of ['port_a_edge_id', 'port_b_edge_id']) if (vx[f] !== undefined && !edgeIds.has(vx[f])) errs.push(`Three-way valve ${vx.id} ${f} ${vx[f]} not in selection`)
    }
    // linked_device_id (heat_exchanger, heat_source_sink, and operation-embedded for control_valve/active devices)
    const linked: unknown = vx.linked_device_id ?? vx.operation?.linked_device_id
    if (linked !== undefined && !vertexIds.has(linked as string)) errs.push(`Device ${vx.id} is linked to device ${linked as string}, which isn't in your selection — add it or remove ${vx.id}`)
    // HX unmodeled-side fluid zones
    const u: Record<string, unknown> | undefined = vx.thermal_calculation?.unmodeled_side
    if (u) {
      for (const f of ['inlet_fluid_zone_id', 'outlet_fluid_zone_id']) {
        if (u[f] !== undefined && !fzIds.has(u[f] as string)) errs.push(`Heat exchanger ${vx.id} ${f} ${u[f] as string} missing`)
      }
    }
  }
  return errs
}
